import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  CreateDrilldownFilterPresetRequest,
  CreateFilterPresetRequest,
  DashboardMode,
  DrilldownFilterPresetDto,
  FilterPresetDto,
  FilterPresetScope,
  FilterPresetSurface,
} from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { authFetch } from "@/lib/api-client";

export const filterPresetKeys = {
  all: ["filter-presets"] as const,
  lists: () => [...filterPresetKeys.all, "list"] as const,
  list: (surface: FilterPresetSurface, mode: DashboardMode, tenantId: string | null) =>
    [...filterPresetKeys.lists(), surface, mode, tenantId] as const,
};

type ListedPreset = FilterPresetDto | DrilldownFilterPresetDto;

function surfaceModeListKey(surface: FilterPresetSurface, mode: DashboardMode) {
  return [...filterPresetKeys.lists(), surface, mode] as const;
}

function presetScope(preset: { scope?: FilterPresetScope }): FilterPresetScope {
  return preset.scope === "company" ? "company" : "personal";
}

function mergeScopedList<T extends ListedPreset>(
  previous: T[],
  scoped: T[],
  scope: FilterPresetScope,
): T[] {
  const remaining = previous.filter((preset) => presetScope(preset) !== scope);
  if (scope === "company") return [...scoped, ...remaining];
  const company = remaining.filter((preset) => presetScope(preset) === "company");
  const rest = remaining.filter((preset) => presetScope(preset) !== "company");
  return [...company, ...scoped, ...rest];
}

function upsertListedPreset<T extends ListedPreset>(old: T[], saved: T): T[] {
  if (old.some((preset) => preset.id === saved.id)) {
    return old.map((preset) => (preset.id === saved.id ? saved : preset));
  }
  if (presetScope(saved) === "company") {
    const company = old.filter((preset) => presetScope(preset) === "company");
    const personal = old.filter((preset) => presetScope(preset) !== "company");
    return [...company, saved, ...personal];
  }
  return [...old, saved];
}

function listQueryFilter(
  surface: FilterPresetSurface,
  mode: DashboardMode,
  tenantId: string | null,
  scope: FilterPresetScope,
) {
  return {
    queryKey:
      scope === "company"
        ? filterPresetKeys.list(surface, mode, tenantId)
        : surfaceModeListKey(surface, mode),
  };
}

function capturedTenantId(): string | null {
  return useAuth.getState().activeTenantId;
}

function tenantHeaders(tenantId: string | null): Record<string, string> {
  return tenantId ? { "X-Tenant-Id": tenantId } : {};
}

function applyOptimisticReorder<T extends ListedPreset>(
  old: T[],
  ids: string[],
  scope: FilterPresetScope,
): T[] {
  const scoped = old.filter((preset) => presetScope(preset) === scope);
  const byId = new Map(scoped.map((preset) => [preset.id, preset]));
  const nextScoped = ids.flatMap((id) => {
    const preset = byId.get(id);
    return preset ? [preset] : [];
  });
  return mergeScopedList(old, nextScoped, scope);
}

function listPresetsUrl(surface: FilterPresetSurface, mode: DashboardMode): string {
  return `/${surface}/filter-presets?mode=${encodeURIComponent(mode)}&includeCompany=true`;
}

async function fetchOverviewFilterPresets(
  mode: DashboardMode,
  tenantId: string | null,
): Promise<FilterPresetDto[]> {
  const { data } = await authFetch<FilterPresetDto[]>(listPresetsUrl("overview", mode), {
    headers: tenantHeaders(tenantId),
    shouldOmitTenantHeader: tenantId === null,
  });
  return data ?? [];
}

async function fetchDrilldownFilterPresets(
  mode: DashboardMode,
  tenantId: string | null,
): Promise<DrilldownFilterPresetDto[]> {
  const { data } = await authFetch<DrilldownFilterPresetDto[]>(listPresetsUrl("drilldown", mode), {
    headers: tenantHeaders(tenantId),
    shouldOmitTenantHeader: tenantId === null,
  });
  return data ?? [];
}

export function overviewFilterPresetsQueryOptions(
  mode: DashboardMode,
  tenantId: string | null,
) {
  return queryOptions({
    queryKey: filterPresetKeys.list("overview", mode, tenantId),
    queryFn: ({ queryKey }) => fetchOverviewFilterPresets(queryKey[3], queryKey[4]),
  });
}

export function drilldownFilterPresetsQueryOptions(
  mode: DashboardMode,
  tenantId: string | null,
) {
  return queryOptions({
    queryKey: filterPresetKeys.list("drilldown", mode, tenantId),
    queryFn: ({ queryKey }) => fetchDrilldownFilterPresets(queryKey[3], queryKey[4]),
  });
}

export function useCreateOverviewFilterPreset(mode: DashboardMode) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFilterPresetRequest & { shareWithCompany?: boolean }) => {
      const requestTenantId = capturedTenantId();
      const { shareWithCompany, ...payload } = body;
      const path = shareWithCompany
        ? "/overview/filter-presets/shared"
        : "/overview/filter-presets";
      const { data } = await authFetch<FilterPresetDto>(path, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: tenantHeaders(requestTenantId),
      });
      const saved = data!;
      queryClient.setQueriesData<FilterPresetDto[]>(
        listQueryFilter("overview", mode, requestTenantId, presetScope(saved)),
        (old = []) => upsertListedPreset(old, saved),
      );
      return saved;
    },
  });
}

export function useCreateDrilldownFilterPreset(mode: DashboardMode) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateDrilldownFilterPresetRequest & { shareWithCompany?: boolean }) => {
      const requestTenantId = capturedTenantId();
      const { shareWithCompany, ...payload } = body;
      const path = shareWithCompany
        ? "/drilldown/filter-presets/shared"
        : "/drilldown/filter-presets";
      const { data } = await authFetch<DrilldownFilterPresetDto>(path, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: tenantHeaders(requestTenantId),
      });
      const saved = data!;
      queryClient.setQueriesData<DrilldownFilterPresetDto[]>(
        listQueryFilter("drilldown", mode, requestTenantId, presetScope(saved)),
        (old = []) => upsertListedPreset(old, saved),
      );
      return saved;
    },
  });
}

export function useDeleteFilterPreset(surface: FilterPresetSurface, mode: DashboardMode) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, scope }: { id: string; scope: FilterPresetScope }) => {
      const requestTenantId = capturedTenantId();
      const path =
        scope === "company"
          ? `/${surface}/filter-presets/shared/${encodeURIComponent(id)}`
          : `/${surface}/filter-presets/${encodeURIComponent(id)}`;
      await authFetch(path, { method: "DELETE", headers: tenantHeaders(requestTenantId) });
      void queryClient.invalidateQueries(
        listQueryFilter(surface, mode, requestTenantId, scope),
      );
      return scope;
    },
  });
}

export function useReorderFilterPresets(
  surface: FilterPresetSurface,
  mode: DashboardMode,
  scope: FilterPresetScope = "personal",
) {
  const queryClient = useQueryClient();
  const orderPath =
    scope === "company"
      ? `/${surface}/filter-presets/shared/order`
      : `/${surface}/filter-presets/order`;
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const requestTenantId = capturedTenantId();
      const queryFilter = listQueryFilter(surface, mode, requestTenantId, scope);
      await queryClient.cancelQueries(queryFilter);
      const previous = queryClient.getQueriesData<ListedPreset[]>(queryFilter);
      queryClient.setQueriesData<ListedPreset[]>(queryFilter, (old = []) =>
        applyOptimisticReorder(old, ids, scope),
      );
      try {
        const { data } = await authFetch<ListedPreset[]>(orderPath, {
          method: "PUT",
          body: JSON.stringify({ mode, ids }),
          headers: tenantHeaders(requestTenantId),
        });
        const rows = data ?? [];
        queryClient.setQueriesData<ListedPreset[]>(queryFilter, (old = []) =>
          mergeScopedList(old, rows, scope),
        );
        return rows;
      } catch (error) {
        previous.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
        throw error;
      }
    },
  });
}
